import React, { useEffect, useRef, useState } from "react";
import AdminSearchBar from "../../components/AdminSideComponents/AdminSearchBar";
import axiosInstance from "../../utils/axios";
import uploadToCloudinary from "../../utils/cloudinary";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";
import { FiInfo } from "react-icons/fi";
import {
  FaSpinner,
  FaImage,
  FaTrash,
  FaGripVertical,
  FaEdit,
} from "react-icons/fa";
import { useDragReorder } from "../../hooks/useDragReorder";

const NoPartnersView = ({ onAdd }) => (
  <div className="p-8 border border-dashed border-gray-300 rounded-lg bg-white text-center">
    <h3 className="text-gray-700 font-medium mb-2">No partners found</h3>
    <p className="text-gray-500 text-sm mb-4">
      You haven't added any partners yet.
    </p>
    <button
      onClick={onAdd}
      className="px-4 py-2 bg-primaryColor text-white rounded hover:opacity-90"
    >
      + Add Partner
    </button>
  </div>
);

const PartnerCardSkeleton = () => (
  <div className="p-3 bg-white rounded-lg shadow-sm border animate-pulse">
    <div className="flex justify-between items-start mb-2">
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 bg-gray-200 rounded" />
        <div className="w-12 h-3 bg-gray-200 rounded" />
      </div>

      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-gray-200 rounded" />
        <div className="w-8 h-8 bg-gray-200 rounded" />
      </div>
    </div>

    <div className="flex justify-center py-5">
      <div className="w-32 h-32 bg-gray-200 rounded-full" />
    </div>

    <div className="mt-3 flex justify-center">
      <div className="w-20 h-5 bg-gray-200 rounded" />
    </div>
  </div>
);

const ImageUploadModal = ({ initialLogo, onClose, onSave }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(initialLogo || "");
  const [uploading, setUploading] = useState(false);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleUpload = async () => {
    if (!file) return toast.error("Pick an image first");
    setUploading(true);
    try {
      const url = await uploadToCloudinary(file);
      onSave(url);
      toast.success("Image uploaded");
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-lg shadow-lg p-6 relative">
        <h3 className="text-lg font-semibold mb-3 text-primaryTextColor">
          Change Partner Logo
        </h3>

        <div className="mb-3">
          <div className="text-sm text-gray-600 mb-2">
            Recommended size: <strong>300 x 300</strong>. Square images work
            best.
          </div>

          <div className="w-full h-48 rounded-md border border-gray-200 overflow-hidden flex items-center justify-center bg-gray-50">
            {preview ? (
              <img
                src={preview}
                alt="preview"
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="text-gray-400">No preview</div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label className="px-3 py-2 bg-gray-100 border rounded cursor-pointer">
            <input
              type="file"
              accept="image/*"
              onChange={handleFile}
              className="hidden"
            />
            <div className="flex items-center gap-2">
              <FaImage /> Choose Image
            </div>
          </label>

          <button
            onClick={handleUpload}
            className="px-4 py-2 bg-primaryColor text-white rounded hover:opacity-90 disabled:opacity-60"
            disabled={uploading || !file}
          >
            {uploading ? (
              <span className="inline-flex items-center gap-2">
                <FaSpinner className="animate-spin" /> Uploading
              </span>
            ) : (
              "Upload & Save"
            )}
          </button>

          <button onClick={onClose} className="px-3 py-2 border rounded">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const AddEditModal = ({ initialData = null, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: "",
    logo: "",
    sequence: 0,
    isActive: true,
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initialData) {
      setForm({
        name: initialData.name || "",
        logo: initialData.logo || "",
        sequence: initialData.sequence ?? 0,
        isActive:
          typeof initialData.isActive === "boolean"
            ? initialData.isActive
            : true,
        _id: initialData._id,
      });
      setPreview(initialData.logo || "");
    } else {
      setForm((p) => ({ ...p, sequence: 0, isActive: true }));
      setPreview("");
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "sequence") {
      setForm((p) => ({ ...p, sequence: value === "" ? "" : Number(value) }));
    } else if (name === "isActive") {
      setForm((p) => ({ ...p, isActive: value === "true" }));
    } else {
      setForm((p) => ({ ...p, [name]: value }));
    }
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleUploadAndGetUrl = async () => {
    if (!file) return form.logo;
    setUploading(true);
    try {
      const url = await uploadToCloudinary(file);
      return url;
    } catch (err) {
      console.error(err);
      toast.error("Image upload failed");
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const logoUrl = await handleUploadAndGetUrl();
      const payload = {
        name: form.name.trim(),
        logo: logoUrl || "",
        sequence: Number(form.sequence) || 0,
        isActive: !!form.isActive,
      };

      let resp;
      if (form._id) {
        resp = await axiosInstance.put(`/partners/${form._id}`, payload);
      } else {
        resp = await axiosInstance.post(`/partners`, payload);
      }
      const createdOrUpdated = resp?.data?.data ?? resp?.data;
      toast.success(form._id ? "Updated" : "Created");
      onSaved(createdOrUpdated);
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg p-6 relative">
        <h3 className="text-lg font-semibold mb-3 text-primaryTextColor">
          {form._id ? "Edit Partner" : "Add New Partner"}
        </h3>

        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <div>
            <label className="block text-sm font-medium mb-1">Name *</label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              className="w-full border px-3 py-2 rounded"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Sequence</label>
            <input
              name="sequence"
              type="number"
              min="0"
              value={form.sequence}
              onChange={handleChange}
              className="w-full border px-3 py-2 rounded"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">Logo</label>
            <div className="flex items-center gap-3">
              <label className="w-28 h-28 relative rounded-md border-dashed overflow-hidden border border-gray-400 flex items-center justify-center cursor-pointer">
                {preview ? (
                  <img
                    src={preview}
                    alt="preview"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="text-gray-400">Click to upload</div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFile}
                  className="hidden"
                />
                {uploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </label>

              <div className="text-sm text-gray-600">
                Recommended: <strong>300 x 300</strong>. Square images look
                best.
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <label className="block text-sm font-medium">Status</label>
            <select
              name="isActive"
              value={String(form.isActive)}
              onChange={(e) =>
                handleChange({
                  target: { name: "isActive", value: e.target.value },
                })
              }
              className="border px-3 py-2 rounded"
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div className="md:col-span-2 flex justify-end gap-3 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded border"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="px-4 py-2 rounded bg-primaryColor text-white disabled:opacity-60"
              disabled={saving}
            >
              {saving ? (
                <span className="inline-flex items-center gap-2">
                  <FaSpinner className="animate-spin" /> Saving
                </span>
              ) : form._id ? (
                "Save Changes"
              ) : (
                "Create"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PartnerCard = ({ partner, onEdit, onDelete, onOpenLogoModal }) => {
  return (
    <div className="relative bg-white rounded-xl shadow-md transition-all duration-200 overflow-hidden border border-gray-100">
      <div className="flex items-center  w-full justify-between px-4 py-2">
        <div className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer">
          <FaGripVertical /> Seq {partner.sequence ?? 0}
        </div>
        <div
          className={`text-xs px-2 py-1 rounded ${
            partner.isActive
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-gray-700"
          }`}
        >
          {partner.isActive ? "active" : "inactive"}
        </div>
      </div>
      <div className="relative p-6 h-48 flex flex-col items-center justify-center">
        {partner.logo ? (
          <div className="w-full h-full flex items-center justify-center">
            <img
              draggable={false}
              src={partner.logo}
              alt={`${partner.name} logo`}
              className="max-w-full max-h-32 object-contain"
            />
          </div>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 mb-4 rounded-full bg-gradient-to-br from-primaryColor to-primaryColor/70 flex items-center justify-center">
              <span className="text-2xl h-full flex items-center justify-center font-bold text-white">
                {partner.name?.charAt(0) ?? "P"}
              </span>
            </div>
            <h3 className="text-primaryTextColor font-medium text-base leading-snug tracking-wide">
              {partner.name}
            </h3>
          </div>
        )}
      </div>

      <div className="px-4 pb-2 flex items-center justify-end gap-2">
        <div className="flex items-center gap-2">
          <button
            title="Edit"
            onClick={() => onEdit(partner)}
            className="p-2 rounded hover:bg-gray-100"
          >
            <FaEdit />
          </button>

          <button
            title="Delete"
            onClick={() => onDelete(partner)}
            className="p-2 rounded hover:bg-red-50 text-red-600"
          >
            <FaTrash />
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminPartners = () => {
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [logoModalItem, setLogoModalItem] = useState(null);
  const {
    list: partners,
    orderChanged,
    initialize,
    onDragStart,
    onDragOver,
    onDrop,
    cancelOrder,
    saveOrder,
    setList: setPartners,
  } = useDragReorder();

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const resp = await axiosInstance.get("/partners/admin");
      const data = resp?.data?.data ?? resp?.data ?? [];
      const sorted = Array.isArray(data)
        ? data.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
        : [];
      initialize(sorted);
    } catch (err) {
      console.error("Fetch partners error:", err);
      toast.error("Failed to load partners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddClick = () => {
    setEditItem(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (item) => {
    setEditItem(item);
    setIsModalOpen(true);
  };

  const handleSaved = (saved) => {
    setPartners((prev) => {
      const id = saved._id || saved.id;
      const found = prev.find((p) => p._id === id);
      if (found) {
        return prev
          .map((p) => (p._id === id ? saved : p))
          .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
      }
      return [...prev, saved].sort(
        (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)
      );
    });
  };

  const handleDelete = (item) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Delete "${item.name}"? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axiosInstance.delete(`/partners/${item._id}`);
          setPartners((p) => p.filter((x) => x._id !== item._id));
          toast.success("Deleted");
        } catch (err) {
          console.error(err);
          toast.error("Delete failed");
        }
      }
    });
  };

  // const handleToggleStatus = async (item) => {
  //   try {
  //     const resp = await axiosInstance.put(`/partners/${item._id}`, {
  //       isActive: !item.isActive,
  //     });
  //     const updated = resp?.data?.data ?? resp?.data;
  //     setPartners((prev) =>
  //       prev.map((p) => (p._id === item._id ? updated : p))
  //     );
  //     toast.success("Status updated");
  //   } catch (err) {
  //     console.error(err);
  //     toast.error("Failed to update status");
  //   }
  // };

  const saveToServer = async (updatedList) => {
    try {
      await Promise.all(
        updatedList.map((c) =>
          axiosInstance.put(`/partners/${c._id}`, { sequence: c.sequence })
        )
      );
      toast.success("Order saved");
    } catch (err) {
      console.error("Failed to update sequence", err);
      toast.error("Failed to save order. Refreshing.");
      fetchPartners();
    }
  };

  const handleSaveOrder = () => saveOrder(saveToServer);

  const openLogoModal = (item) => {
    setLogoModalItem(item);
  };

  const handleLogoSaved = async (newUrl) => {
    if (!logoModalItem) return;
    try {
      const resp = await axiosInstance.put(`/partners/${logoModalItem._id}`, {
        logo: newUrl,
      });
      const updated = resp?.data?.data ?? resp?.data;
      setPartners((prev) =>
        prev.map((p) => (p._id === logoModalItem._id ? updated : p))
      );
      toast.success("Logo updated");
    } catch (err) {
      console.error(err);
      toast.error("Update failed");
    } finally {
      setLogoModalItem(null);
    }
  };

  const handleSearch = (q) => {
    if (!q) {
      fetchPartners();
      return;
    }
    const lower = q.toLowerCase();
    setPartners((prev) =>
      prev.filter((it) => (it.name || "").toLowerCase().includes(lower))
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminSearchBar
        title="Partners Management"
        onAddNewClick={handleAddClick}
        onSearchClick={handleSearch}
        hideAddButton={false}
      />

      <div className="px-6 pb-6">
        <div className="my-2 flex items-center gap-2 text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded">
          <FiInfo className="text-primaryColor min-w-4 min-h-4" />
          <span>
            Manage partner entries. Click <strong>Edit</strong> to update name,
            logo, status or sequence. Drag cards to change order.
          </span>
        </div>
        {orderChanged && (
          <div className="px-6 py-3 mb-2 bg-yellow-100 border-b border-yellow-300 flex items-center justify-between">
            <div className="flex items-center gap-2 text-yellow-800">
              <FiInfo />
              <span>Order changed. Save changes?</span>
            </div>
            <div className="flex gap-3">
              <button
                className="px-4 py-1 bg-primaryColor text-white rounded"
                onClick={handleSaveOrder}
              >
                Save Order
              </button>
              <button
                className="px-4 py-1 border rounded"
                onClick={cancelOrder}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {loading ? (
          <div className="grid  grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <PartnerCardSkeleton key={i} />
            ))}
          </div>
        ) : partners.length === 0 ? (
          <div className="mt-6">
            <NoPartnersView onAdd={handleAddClick} />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <AnimatePresence>
              {partners.map((item, idx) => (
                <motion.div
                  key={item._id}
                  layout
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                  onDragOver={onDragOver}
                  onDrop={(e) => onDrop(e, idx)}
                  draggable
                  onDragStart={(e) => onDragStart(e, idx)}
                >
                  <PartnerCard
                    partner={item}
                    onEdit={handleEditClick}
                    onDelete={handleDelete}
                    onOpenLogoModal={openLogoModal}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {isModalOpen && (
        <AddEditModal
          initialData={editItem}
          onClose={() => {
            setIsModalOpen(false);
            setEditItem(null);
          }}
          onSaved={handleSaved}
        />
      )}

      {logoModalItem && (
        <ImageUploadModal
          initialLogo={logoModalItem.logo}
          onClose={() => setLogoModalItem(null)}
          onSave={handleLogoSaved}
        />
      )}
    </div>
  );
};

export default AdminPartners;
